import { Box } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { logError } from 'lib/sentry/logError';
import { Flex } from 'shared_DEPRECATED/components/Flex';

import { ChatMessageAudioDuration } from './AudioDuration';
import { ChatMessageAudioLoader } from './AudioLoader';
import { ChatMessageAudioPlayPauseButton } from './AudioPlayPauseButton';
import { ChatMessageAudioWave } from './AudioWave';

const MEDIA_EVENTS = {
  LOADED_METADATA: 'loadedmetadata',
  TIME_UPDATE: 'timeupdate',
  ENDED: 'ended',
};

type ChatMessageAudioPlayerProps = {
  src: string;
  duration: number;
  children: (args: {
    isPlaying: boolean;
    togglePlay: () => Promise<void>;
    audioDuration: number;
  }) => React.ReactNode;
};

const ChatMessageAudioPlayer = ({
  src,
  duration,
  children,
}: ChatMessageAudioPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState<number | undefined>(0);

  const handleTimeUpdate = useCallback(() => {
    const newCurrentTime = audioRef.current?.currentTime;
    setCurrentTime(newCurrentTime || 0);
  }, [audioRef]);

  const handleStop = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  useEffect(() => {
    let audioReference = audioRef.current;

    audioReference?.addEventListener(
      MEDIA_EVENTS.TIME_UPDATE,
      handleTimeUpdate
    );
    audioReference?.addEventListener(MEDIA_EVENTS.ENDED, handleStop);

    return () => {
      audioReference?.removeEventListener(
        MEDIA_EVENTS.TIME_UPDATE,
        handleTimeUpdate
      );
      audioReference?.removeEventListener(MEDIA_EVENTS.ENDED, handleStop);
    };
  }, [handleTimeUpdate, handleStop]);

  const togglePlay = async () => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      try {
        await audioRef.current?.play();
        setIsPlaying(true);
      } catch (error) {
        logError(error);
      }
    }
  };

  // if we don't have currentTime or it is 0 that means we haven't started audio playback yet and we display total duration
  // otherwise we display current elapsed time of audio recording
  const audioDuration =
    currentTime && currentTime > 0 ? currentTime : duration || 0;

  return (
    <Box w="210px">
      <audio src={src} ref={audioRef} />
      <Flex gap="var(--spacing01)">
        {children({ isPlaying, togglePlay, audioDuration })}
      </Flex>
    </Box>
  );
};

ChatMessageAudioPlayer.PlayPauseButton = ChatMessageAudioPlayPauseButton;
ChatMessageAudioPlayer.Wave = ChatMessageAudioWave;
ChatMessageAudioPlayer.AudioDuration = ChatMessageAudioDuration;
ChatMessageAudioPlayer.Loader = ChatMessageAudioLoader;

export { ChatMessageAudioPlayer };
